<template>
  <div class="stepper-form-container">
    <FlyoutMenuHead :selectedItem="selectedItem" route="operators" class="ml-8" />

    <v-card flat class="form-card">
      <v-card-title></v-card-title>

      <ReusableStepper
        v-model="currentStep"
        :steps="steps"
        :width="900"
        :editable="true"
        :usePathIcon="false"
        :can-navigate="guardStepper"
      >
        <template #step="{ step }">
          <BasicOperatorInfo
            v-if="step === 1"
            ref="step1"
            :form="form"
            @update:form="updateForm"
            class="ml-8"
          />
          <AdvancedOperatorInfo
            v-else-if="step === 2"
            ref="step2"
            :form="form"
            @update:form="updateForm"
          />
          <OperatorCredentials
            v-else-if="step === 3"
            ref="step3"
            :form="form"
            @update:form="updateForm"
          />
        </template>
      </ReusableStepper>
    </v-card>

    <FormFooter
      :current-step="currentStep"
      :total-steps="steps.length"
      @next="onNext"
      @prev="prevStep"
      @save="saveOperator"
      @cancel="handleCancel"
    />
  </div>
</template>

<script>
import BasicOperatorInfo from './BasicOperatorInfoForm.vue';
import AdvancedOperatorInfo from './AdvancedOperatorInfoForm.vue';
import OperatorCredentials from './OperatorCredentialsForm.vue';
import FormFooter from './FormFooter.vue';
import FlyoutMenuHead from '../FlyoutMenuHead.vue';
import ReusableStepper from './ReusableStepper.vue';
import { TokenStorage } from '@/services/TokenStorage';
import { dataLoadingMixin } from '@/mixins/dataLoading';
import { mapActions } from 'vuex';

export default {
  name: 'CreateOperatorForm',
  mixins: [dataLoadingMixin],
  components: {
    BasicOperatorInfo,
    AdvancedOperatorInfo,
    OperatorCredentials,
    FormFooter,
    FlyoutMenuHead,
    ReusableStepper,
  },
  props: { progress: { type: [String, Number], default: 1 } },
  data() {
    return {
      selectedItem: { name: 'Create operator' },
      currentStep: 1,
      saving: false,
      steps: [
        { title: 'Main info' },
        { title: 'Additional details' },
        { title: 'Login credentials' },
      ],
      form: {
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        gender: '',
        email: '',
        phoneNumber: '',
        enabled: true,
        locations: [],
        visionId: '',
        externalId: '',
        tag_uuids: [],
        operator_group_uuids: [],
        username: '',
        password: '',
        // Custom fields 1-8 (will be populated dynamically)
        customField1: '',
        customField2: '',
        customField3: '',
        customField4: '',
        customField5: '',
        customField6: '',
        customField7: '',
        customField8: '',
      },
    };
  },
  watch: {
    progress(newVal) { this.currentStep = parseInt(newVal) || 1; },
  },
  mounted() {
    this.currentStep = parseInt(this.progress) || 1;
    
    // Fetch necessary data for the form when component is mounted
    this.initializeFormData();
  },
  methods: {
    ...mapActions({
      addAlert: 'global/addAlert',
    }),
    updateForm(updatedData) {
      this.form = { ...this.form, ...updatedData };
    },
    
    async initializeFormData() {      
      try {
        // Use cached fetching to prevent duplicate API calls
        await Promise.all([
          this.$fetchIfNeeded('operators/fetchSpaceCustom_s', 'operators/getOperatorCustom_s'),
          this.$fetchIfNeeded('operators/fetchOperatorGroups', 'operators/getOperatorGroups'),  
          this.$fetchIfNeeded('locations/getLocationForLists', 'locations/getLocations'),
          this.$fetchIfNeeded('tags/getTags', 'tags/tags', 'operator', { force: true })
        ]);
        
      } catch (error) {
        console.error('Error initializing form data:', error);
      }
    },
    
    // guard koji koristi step-ove forme
    async guardStepper(from, to) {
      if (to <= from) return true; // unazad uvijek smijemo
      return this.validateStep(from);
    },

    async onNext() {
      // Sync current step's data before validating
      const map = { 1: 'step1', 2: 'step2', 3: 'step3' };
      const ref = this.$refs[map[this.currentStep]];
      
      if (ref && typeof ref.emitChange === 'function') {
        ref.emitChange();
      }
      
      // Wait for parent form to update
      await this.$nextTick();
      
      const isValid = await this.validateStep(this.currentStep);
      
      if (isValid) {
        this.nextStep();
      }
    },

    nextStep() {
      if (this.currentStep < this.steps.length) this.currentStep++;
    },
    prevStep() { if (this.currentStep > 1) this.currentStep--; },

    // validacija po koraku
    validateStep(step) {
      const map = { 1: 'step1', 2: 'step2', 3: 'step3' };
      const ref = this.$refs[map[step]];
      if (!ref || typeof ref.validate !== 'function') return true;
      return ref.validate();
    },

    async saveOperator() {
      // Clear any stale custom-field errors from a previous failed attempt
      if (this.$refs.step2 && typeof this.$refs.step2.clearFieldErrors === 'function') {
        this.$refs.step2.clearFieldErrors();
      }

      // Sync all form data from child components
      if (this.$refs.step1 && this.$refs.step1.emitChange) {
        this.$refs.step1.emitChange();
      }
      if (this.$refs.step2 && this.$refs.step2.emitChange) {
        this.$refs.step2.emitChange();
      }
      if (this.$refs.step3 && this.$refs.step3.emitChange) {
        this.$refs.step3.emitChange();
      }

      // Wait for next tick to ensure all updates are processed
      await this.$nextTick();


      // provjeri sve korake
      for (let s = 1; s <= 3; s++) {
        const ok = await this.validateStep(s);
        if (!ok) {
          this.currentStep = s;
          this.addAlert({ message: 'Please complete required fields', type: 'error' });
          return;
        }
      }

      this.saving = true;
      try {
        // Try multiple sources to get the current user's email
        const createdByEmail =
          this.$store.getters.getUserEmail ||
          TokenStorage.getEmail() ||
          '';

        if (!createdByEmail) {
          this.addAlert({ message: 'Could not determine user email', type: 'error' });
          return;
        }

        await this.$store.dispatch('operators/createOperator', {
          form: this.form,
          createdByEmail,
        });

        this.addAlert({ message: 'Operator created successfully', type: 'success' });
        
        // Navigate back to operators list after successful creation
        setTimeout(() => {
          this.$router.push('/operators');
        }, 2000);
      } catch (e) {
        if (this.handleCustomFieldError(e)) return;
        const errorMsg = this.parseErrorMessage(e);
        this.addAlert({ message: errorMsg, type: 'error' });
      } finally {
        this.saving = false;
      }
    },

    // Detects backend errors (409 conflict / 400 validation) tied to a specific
    // operator custom field, maps the message onto that field in the Additional
    // Details step, and navigates the user back to it. Returns true if the error
    // was handled this way, false if it should fall back to generic handling.
    handleCustomFieldError(error) {
      const response = error.response;
      if (!response) return false;

      // 409 Conflict - duplicate custom field value, e.g.
      // "Operator with same 'operator_custom_1' field already exists"
      if (response.status === 409) {
        const message = response.data?.message;
        const match = message && message.match(/operator_custom_\d+/);
        if (match) {
          this.applyCustomFieldError(match[0], message);
          return true;
        }
      }

      // 400 validation errors - field map, e.g. { errors: { operator_custom_1: '...' } }
      if (response.status === 400) {
        const errData = response.data || {};
        const errorsObj = errData.errors || errData;
        const customKey = Object.keys(errorsObj).find((key) => /^operator_custom_\d+$/.test(key));
        if (customKey) {
          const raw = errorsObj[customKey];
          const message = Array.isArray(raw) ? raw[0] : raw;
          this.applyCustomFieldError(customKey, message);
          return true;
        }
      }

      return false;
    },

    applyCustomFieldError(fieldKey, message) {
      if (this.$refs.step2 && typeof this.$refs.step2.setFieldError === 'function') {
        this.$refs.step2.setFieldError(fieldKey, message);
      }
      this.currentStep = 2; // Additional details step
    },

    parseErrorMessage(error) {
      // Try to extract a meaningful error message from the API response
      if (error.response?.data) {
        const data = error.response.data;
        
        // Check for common error message formats
        if (data.message) return data.message;
        if (data.error) return data.error;
        if (data.detail) return data.detail;
        
        // Handle validation errors (array of errors)
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors.map(err => err.message || err).join(', ');
        }
        
        // Handle object with field errors
        if (typeof data === 'object' && !Array.isArray(data)) {
          const messages = Object.entries(data)
            .filter(([key]) => key !== 'status' && key !== 'statusCode')
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          if (messages) return messages;
        }
      }
      
      // Fallback to error message or generic message
      return error.message || 'Failed to create operator';
    },
    
    handleCancel() {
      this.$router.push('/operators');
    },
  },
};
</script>

<style scoped>
.stepper-form-container { overflow: visible; }
.form-card { background: transparent !important; }

.stepper-form-container :deep(.ns-stepper),
.stepper-form-container :deep(.v-stepper__header) {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
